import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { data as dataApi } from '../api-client/index';
import PageHeader from '../components/common/PageHeader';
import SettingsIcon from '@mui/icons-material/Settings';
export default function Settings() {
    const fileInputRef = useRef(null);
    const [importChoiceOpen, setImportChoiceOpen] = useState(false);
    const [importJson, setImportJson] = useState(null);
    const [installPrompt, setInstallPrompt] = useState(null);
    useEffect(() => {
        const onBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        const onAppInstalled = () => {
            setInstallPrompt(null);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onAppInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);
    const handleInstallClick = async () => {
        if (!installPrompt)
            return;
        try {
            await installPrompt.prompt();
            await installPrompt.userChoice;
        }
        finally {
            setInstallPrompt(null);
        }
    };
    const handleExport = async () => {
        try {
            const bundle = await dataApi.exportAll();
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `salt-track-backup-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        }
        catch (e) {
            console.error('Export failed', e);
        }
    };
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };
    const onFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        try {
            const text = await file.text();
            const json = JSON.parse(text);
            setImportJson(json);
            setImportChoiceOpen(true);
        }
        catch (err) {
            console.error('Invalid file selected', err);
        }
        finally {
            e.target.value = '';
        }
    };
    const proceedImport = async (mode) => {
        if (!importJson)
            return;
        try {
            await dataApi.importAll(importJson, mode);
            setImportChoiceOpen(false);
            setImportJson(null);
            window.location.reload();
        }
        catch (err) {
            console.error('Import failed', err);
        }
    };
    return (_jsxs(Box, { children: [_jsx(PageHeader, { title: "Settings", subtitle: "Manage backups and app preferences", icon: SettingsIcon }), _jsx(Card, { sx: { maxWidth: 600 }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "Data Backup" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Export your data to a JSON file for backup, or import a previous backup to restore your data." }), _jsxs(Box, { sx: { display: 'flex', gap: 2, flexWrap: 'wrap' }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(DownloadIcon, {}), onClick: handleExport, "aria-label": "Export data", children: "Export Data" }), _jsx(Button, { variant: "outlined", startIcon: _jsx(UploadIcon, {}), onClick: handleImportClick, "aria-label": "Import data", children: "Import Data" })] }), _jsx("input", { ref: fileInputRef, type: "file", accept: "application/json", style: { display: 'none' }, onChange: onFileSelected })] }) }), _jsx(Card, { sx: { maxWidth: 600, mt: 2 }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "Install App" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Install Salt Track as an app on your device for quick access and offline use." }), _jsx(Button, { variant: "outlined", startIcon: _jsx(InstallDesktopIcon, {}), onClick: handleInstallClick, disabled: !installPrompt, "aria-label": "Install app", children: installPrompt ? 'Install App' : 'Already Installed' })] }) }), _jsxs(Dialog, { open: importChoiceOpen, onClose: () => setImportChoiceOpen(false), "aria-labelledby": "import-choice-title", children: [_jsx(DialogTitle, { id: "import-choice-title", children: "Import Data" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { children: "Choose how to import: Merge upserts records by id; Overwrite clears existing data first." }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setImportChoiceOpen(false), color: "inherit", children: "Cancel" }), _jsx(Button, { onClick: () => proceedImport('merge'), variant: "outlined", children: "Merge" }), _jsx(Button, { onClick: () => proceedImport('overwrite'), color: "error", variant: "contained", children: "Overwrite" })] })] })] }));
}
